import { DynamicEvent } from 'nexgen-types';
import {
  useMeetingStore,
  useNotificationStore,
  useRecordingStore,
  useWorkplaceStore,
} from '../../../host/stores';
import { useLiveCaptionsStore, TranscriptEntry } from '../../../host/stores/liveCaptions.store';
import { MeetingEventRouter } from '../events/MeetingEventRouter';
import Swal from 'sweetalert2';
import { jitsiService } from '../../../shared/singletons/jitsi-singleton';
import { ApiService } from '../../../shared/services/api.service';
import { useBreakStore } from '@/host/workplace/stores/BreakStore';
import {
  assemblyAIStreamingService,
  TranscriptionEvent,
} from '../../../shared/services/AssemblyAIStreamingService';
import { transcriptCollectionService } from '../../../shared/services/TranscriptCollectionService';
import { translationService } from '../../../shared/services/translation.service';
import { nanoid } from 'nanoid';
import { useMultiFloorStateStore } from '@/apps/office/stores/MultiFloorStore';
import { Logger } from '../../../shared/services/logger.service';
import { formatDateTime } from '@/apps/recording/components/Recordings/dateUtils';
import { GlobalAudioNotificationService } from '@/host/services/audio-notification.service';
import { isCurrentUserGuest } from '@/shared/utils/guest-detection.util';
import {
  trackRecordingStarted,
  trackRecordingStopped,
  trackTranscriptionStarted,
  trackTranscriptionStopped,
  type RecordingStopReason,
  type TranscriptionStopReason,
} from '@host/utils/recording-tracking';
export class MeetingService {
  private static _instance: MeetingService;
  meetingEventRouter?: MeetingEventRouter;

  // State watchers for auto-streaming
  private recordingStateUnsubscribe?: () => void;
  private manualTranscriptionUnsubscribe?: () => void;

  // Track readiness state for handling late joiners
  private localTracksReady: boolean = false;
  private trackReadyCallbacks: Array<() => void> = [];

  static get instance(): MeetingService {
    if (!MeetingService._instance) {
      MeetingService._instance = new MeetingService();
    }
    return MeetingService._instance;
  }

  static get eventRouter(): MeetingEventRouter {
    return MeetingService._instance.meetingEventRouter!;
  }

  setEventRouterInstance(eventRouterInstance: MeetingEventRouter) {
    this.meetingEventRouter = eventRouterInstance;
  }

  /**
   * Initialize auto-streaming watcher that monitors recording state changes
   * and automatically starts/stops streaming based on recording + transcription status
   * OR manual transcription toggle
   */
  initializeAutoStreamingWatcher(): void {
    Logger.debug('[MeetingService] Initializing auto-streaming watcher');

    // Watch for recording state changes (can_transcribe, activeRecordingInfo)
    this.recordingStateUnsubscribe = useRecordingStore.subscribe(
      (state) => state.activeRecordingInfo,

      async () => {
        await this.handleRecordingStateChange();
      }
    );

    // Watch for manual transcription state changes
    this.manualTranscriptionUnsubscribe = useRecordingStore.subscribe(
      (state) => state.isManualTranscriptionActive,

      async () => {
        await this.handleTranscriptionStateChange();
      }
    );
  }

  /**
   * Handle recording state changes and determine if streaming should start/stop
   */
  private async handleRecordingStateChange(): Promise<void> {
    await this.handleTranscriptionStateChange();
  }

  /**
   * Unified handler for transcription state changes
   * Checks BOTH recording-based and manual transcription conditions
   */
  private async handleTranscriptionStateChange(): Promise<void> {
    const { recordingSessionId } = useMeetingStore.getState();
    const { roomRecordingStatus, activeRecordingInfo, isManualTranscriptionActive } =
      useRecordingStore.getState();

    const isRecordingActive = recordingSessionId && roomRecordingStatus === 'on';
    const canTranscribe = activeRecordingInfo?.can_transcribe;
    const isRecordingTranscription = isRecordingActive && canTranscribe;

    console.log('[MeetingService] Transcription state changed:', {
      isRecordingActive,
      canTranscribe,
      isRecordingTranscription,
      isManualTranscriptionActive,
      sessionId: recordingSessionId,
      roomRecordingStatus,
    });

    // Start streaming if EITHER condition is true:
    // 1. Recording is active AND can_transcribe is true (existing behavior)
    // 2. Manual transcription is active (new behavior)
    const shouldTranscribe = isRecordingTranscription || isManualTranscriptionActive;

    if (shouldTranscribe) {
      console.log('[MeetingService] ✅ Conditions met - ensuring streaming started');
      await this.ensureStreamingStarted();
    } else {
      Logger.debug('[MeetingService] ❌ Conditions not met - ensuring streaming stopped');
      await this.ensureStreamingStopped();
    }
  }

  /**
   * Ensure streaming is started (idempotent)
   */
  private async ensureStreamingStarted(): Promise<void> {
    const { getCurrentRoomPid } = useMeetingStore.getState();
    const { activeRecordingInfo } = useRecordingStore.getState();
    const roomPid = getCurrentRoomPid();

    if (!roomPid) {
      console.warn('[MeetingService] No room PID - cannot start streaming');
      return;
    }

    const currentRecordingId = activeRecordingInfo?.recordingPid;
    const isCollectingForDifferentRecording =
      transcriptCollectionService.isCollecting() &&
      transcriptCollectionService.getCurrentMeetingId() !== currentRecordingId;

    // Check if already streaming for this room AND same recording
    if (
      assemblyAIStreamingService.isActive() &&
      assemblyAIStreamingService.getCurrentRoomPid() === roomPid &&
      !isCollectingForDifferentRecording
    ) {
      Logger.debug('[MeetingService] Streaming already active for this room and recording');
      return;
    }

    // Stop any existing stream first (cleanup)
    if (assemblyAIStreamingService.isActive()) {
      Logger.debug('[MeetingService] Stopping existing stream before starting new one');
      await assemblyAIStreamingService.stopTranscription();
    }

    Logger.debug(
      '[MeetingService] 🎤 Auto-starting streaming for room:',
      roomPid,
      'recording:',
      currentRecordingId
    );
    await this.startStreamingTranscription(roomPid);
  }

  /**
   * Ensure streaming is stopped (idempotent)
   */
  private async ensureStreamingStopped(): Promise<void> {
    if (assemblyAIStreamingService.isActive()) {
      Logger.debug('[MeetingService] 🛑 Auto-stopping streaming');
      await this.stopStreamingTranscription();
    } else {
      Logger.debug('[MeetingService] Streaming already stopped');
    }
  }

  /**
   * Cleanup watchers and subscriptions
   */
  cleanup(): void {
    Logger.debug('[MeetingService] Cleaning up watchers');
    this.recordingStateUnsubscribe?.();
    this.manualTranscriptionUnsubscribe?.();
    this.resetTrackReadiness();

    // Reset manual transcription state on cleanup
    useRecordingStore.getState().setManualTranscriptionActive(false);
  }

  /**
   * Wait for local audio tracks to be available
   * Uses a hybrid approach: listens for track events + polling fallback
   * @param timeout Maximum time to wait in milliseconds (default 5000ms)
   * @returns Promise that resolves when audio track is available or rejects on timeout
   */
  private async waitForLocalAudioTrack(timeout: number = 5000): Promise<any> {
    const startTime = Date.now();
    const pollInterval = 100; // Check every 100ms
    const maxAttempts = Math.floor(timeout / pollInterval);

    Logger.debug('[MeetingService] Waiting for local audio track...');

    // First, check if tracks are already available (early joiner case)
    const immediateCheck = jitsiService?.getLocalParticipant();
    const immediateAudioTrack = immediateCheck?.tracks.find((track) => track.type === 'audio');

    if (immediateAudioTrack?.nativeTrack) {
      Logger.debug('[MeetingService] ✅ Audio track already available (early joiner)');
      return immediateAudioTrack.nativeTrack;
    }

    // Not immediately available - use hybrid approach
    return new Promise((resolve, reject) => {
      let attemptCount = 0;
      let resolved = false;

      // Approach 1: Event-driven (primary)
      const eventCallback = () => {
        if (resolved) return;

        const localParticipant = jitsiService?.getLocalParticipant();
        const audioTrack = localParticipant?.tracks.find((track) => track.type === 'audio');

        if (audioTrack?.nativeTrack) {
          resolved = true;
          clearInterval(pollTimer);
          const elapsed = Date.now() - startTime;
          Logger.debug(`[MeetingService] ✅ Audio track ready via event (${elapsed}ms)`);
          resolve(audioTrack.nativeTrack);
        }
      };

      // Register callback for track ready notification
      this.trackReadyCallbacks.push(eventCallback);

      // Approach 2: Polling (fallback safety net)
      const pollTimer = setInterval(() => {
        if (resolved) return;

        attemptCount++;
        const elapsed = Date.now() - startTime;

        const localParticipant = jitsiService?.getLocalParticipant();
        const audioTrack = localParticipant?.tracks.find((track) => track.type === 'audio');

        if (audioTrack?.nativeTrack) {
          resolved = true;
          clearInterval(pollTimer);
          // Remove event callback
          this.trackReadyCallbacks = this.trackReadyCallbacks.filter((cb) => cb !== eventCallback);
          Logger.debug(
            `[MeetingService] ✅ Audio track ready via polling (${elapsed}ms, attempt ${attemptCount})`
          );
          resolve(audioTrack.nativeTrack);
          return;
        }

        // Timeout check
        if (attemptCount >= maxAttempts) {
          resolved = true;
          clearInterval(pollTimer);
          // Remove event callback
          this.trackReadyCallbacks = this.trackReadyCallbacks.filter((cb) => cb !== eventCallback);
          Logger.error(`[MeetingService] ❌ Timeout waiting for audio track after ${elapsed}ms`);
          reject(
            new Error(
              `Audio track not available after ${timeout}ms. Please check microphone permissions.`
            )
          );
        } else if (attemptCount % 10 === 0) {
          Logger.debug(
            `[MeetingService] ⏳ Still waiting for audio track... (${elapsed}ms, attempt ${attemptCount}/${maxAttempts})`
          );
        }
      }, pollInterval);

      // Timeout safety (backstop)
      setTimeout(() => {
        if (!resolved) {
          resolved = true;
          clearInterval(pollTimer);
          this.trackReadyCallbacks = this.trackReadyCallbacks.filter((cb) => cb !== eventCallback);
          reject(new Error(`Hard timeout waiting for audio track after ${timeout}ms`));
        }
      }, timeout + 100);
    });
  }

  /**
   * Notify waiting consumers that local tracks are ready
   * Called by jitsi-meeting.service after tracks are initialized
   */
  notifyLocalTracksReady(): void {
    Logger.debug('[MeetingService] 🎤 Local tracks ready notification');
    this.localTracksReady = true;

    // Trigger all waiting callbacks
    this.trackReadyCallbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        Logger.error('[MeetingService] Error in track ready callback:', error);
      }
    });

    // Clear callbacks after triggering
    this.trackReadyCallbacks = [];

    // Check if transcription should be active now that tracks are ready
    // This handles the race condition where transcription starts before tracks are initialized
    void this.reconcileTranscriptionState();
  }

  /**
   * Reset track readiness state (called on cleanup)
   */
  private resetTrackReadiness(): void {
    this.localTracksReady = false;
    this.trackReadyCallbacks = [];
  }

  /**
   * Reconcile transcription state after local tracks become ready
   * Handles race condition where trancsription starts before tracks are initialized
   */
  private async reconcileTranscriptionState(): Promise<void> {
    const { recordingSessionId } = useMeetingStore.getState();
    const { roomRecordingStatus, activeRecordingInfo, isManualTranscriptionActive } =
      useRecordingStore.getState();

    const isRecordingActive = recordingSessionId && roomRecordingStatus === 'on';
    const canTranscribe = activeRecordingInfo?.can_transcribe;
    const isRecordingTranscription = isRecordingActive && canTranscribe;

    // Check if transcription should be active (recording-based OR manual transcription)
    const shouldTranscribe = isRecordingTranscription || isManualTranscriptionActive;

    // Check if already transcribing
    const isCurrentlyTranscribing = assemblyAIStreamingService.isActive();

    Logger.debug('[MeetingService] 🔄 Reconciling transcription state after tracks ready:', {
      shouldTranscribe,
      isCurrentlyTranscribing,
      isRecordingActive,
      canTranscribe,
      isManualTranscriptionActive,
      hasRecordingInfo: !!activeRecordingInfo,
    });

    // If should be transcribing but aren't, start it now
    if (shouldTranscribe && !isCurrentlyTranscribing) {
      Logger.warn(
        '[MeetingService] 🎯 RACE CONDITION DETECTED: Tracks now ready - starting transcription that was waiting for audio'
      );
      await this.ensureStreamingStarted();
    } else if (!shouldTranscribe) {
      Logger.debug('[MeetingService] ℹ️ No transcription needed (not active)');
    } else if (isCurrentlyTranscribing) {
      Logger.debug('[MeetingService] ✅ Transcription already active, no action needed');
    }
  }

  // Both handlers are reached from four event types each (the room topic, the plain event and
  // the sender's own ack), so they must tolerate an event that carries no emoji payload
  // instead of throwing — a throw here is swallowed by EventService's per-handler try/catch
  // and only surfaces as a console error, which is how the ack bug stayed invisible.
  handleRemoteEmojiOn = (event: DynamicEvent) => {
    const { user_pid, type, room_pid } = (event ?? {}) as any;
    if (!user_pid) return;
    const { updateMeetingEmojiEntry, getCurrentRoomPid } = useMeetingStore.getState();
    if (room_pid == getCurrentRoomPid()) {
      updateMeetingEmojiEntry(user_pid, type);
    }
  };

  handleRemoteEmojiOff = (event: DynamicEvent) => {
    const { user_pid, room_pid } = (event ?? {}) as any;
    if (!user_pid) return;
    const { updateMeetingEmojiEntry, getCurrentRoomPid } = useMeetingStore.getState();
    if (room_pid == getCurrentRoomPid()) {
      updateMeetingEmojiEntry(user_pid);
    }
  };

  handleRoomChangeEvent = (_floorPid: string, roomPid: string, userPid: string) => {
    const { isMeetingHungUp, getCurrentRoomPid, setMeetingHungUpStatus } =
      useMeetingStore.getState();

    const breakDetails = useBreakStore.getState().breakDetails;
    const isOnBreak = !!breakDetails;

    const currentUserPid = useWorkplaceStore.getState().user?.pid;
    if (isMeetingHungUp && currentUserPid !== userPid) {
      const currentUserRoomPid = getCurrentRoomPid();
      if (currentUserRoomPid === roomPid && !isOnBreak) {
        setMeetingHungUpStatus(false, 'someone_entered');
      }
    }
  };

  /**
   * Start manual transcription (independent of recording)
   * This is a PUBLIC method that can be called from UI components
   */
  async startManualTranscription(): Promise<void> {
    const { user } = useWorkplaceStore.getState();
    const { getCurrentRoomPid, getAllParticipantIds, recordingSessionId } =
      useMeetingStore.getState();
    const { roomRecordingStatus, activeRecordingInfo } = useRecordingStore.getState();
    const roomPid = getCurrentRoomPid();

    if (!roomPid) {
      console.error('[MeetingService] Cannot start manual transcription - no room');
      return;
    }

    // Check if there's an active recording with can_transcribe=false
    const isRecordingActive = recordingSessionId && roomRecordingStatus === 'on';
    const canTranscribe = activeRecordingInfo?.can_transcribe;

    if (isRecordingActive && canTranscribe === false && activeRecordingInfo?.recordingPid) {
      // SCENARIO: Recording with can_transcribe=false is ongoing

      // Permission check: Only the recording initiator can convert it to can_transcribe=true
      const isRecordingInitiator = user?.pid === activeRecordingInfo?.initiatorUserPid;
      if (!isRecordingInitiator) {
        return;
      }
      // Initiator can convert their recording to can_transcribe=true
      console.log(
        '[MeetingService] Converting recording to can_transcribe=true:',
        activeRecordingInfo.recordingPid
      );

      try {
        // 1. Update the existing recording to enable transcription
        await ApiService.request<any>({
          method: 'PATCH',
          endpoint: `/recordings/${activeRecordingInfo.recordingPid}`,
          data: {
            can_transcribe: true,
          },
        });

        // 2. Update local state to reflect the change
        const updatedRecordingInfo = {
          ...activeRecordingInfo,
          can_transcribe: true,
        };

        useRecordingStore.getState().setActiveRecordingInfo(updatedRecordingInfo);

        // 3. Broadcast the updated recording info to other participants
        // Pass recordingInfo so they can update their local state, but the event handler
        // will check the recording type to avoid setting manual transcription active
        this.meetingEventRouter?.emitLiveCaptionsToggleEvent(true, updatedRecordingInfo);

        console.log(
          '[MeetingService] ✅ Recording converted to transcription-enabled successfully'
        );
        trackTranscriptionStarted('added_to_recording', getAllParticipantIds().length);

        // Note: We don't set manual transcription active here because transcription
        // is now linked to the recording, not standalone. The event handler will
        // check the recording type and only set manual transcription for standalone types.
      } catch (error) {
        console.error(
          '[MeetingService] ❌ Failed to convert recording to can_transcribe=true:',
          error
        );
        throw error;
      }

      return;
    }

    // SCENARIO: No active recording - Create a new standalone transcription
    console.log('[MeetingService] 🎤 Starting manual transcription for room:', roomPid);

    try {
      // 1. Generate a random session ID for standalone transcription
      const sessionId = crypto.randomUUID();
      const participantCount = getAllParticipantIds().length;

      // 2. Create standalone_transcription recording in backend
      const response = await ApiService.request<any>({
        method: 'POST',
        endpoint: '/recordings',
        data: {
          room_pid: roomPid,
          recording_session_id: sessionId,
          type: 'standalone_transcription',
          can_transcribe: true,
          participant_count: participantCount,
          title: `Transcription - ${new Date().toLocaleString()}`,
        },
      });

      console.log(
        '[MeetingService] ✅ Standalone transcription recording created:',
        response.recording_pid
      );

      // 3. Set active recording info so collection service can use it
      const recordingInfo = {
        name: user?.name || response.name,
        avatarUrl: user?.avatar_url || response.avatar_url,
        initiatorUserPid: user?.pid || response.user_pid,
        recordingStartTime: response.created_at,
        recordingPid: response.recording_pid,
        recordingRoomPid: roomPid,
        title: response.title,
        can_transcribe: true,
        type: response.type,
      };

      useRecordingStore.getState().setActiveRecordingInfo(recordingInfo);

      // 4. Update manual transcription state - this will trigger the auto-watcher
      useRecordingStore.getState().setManualTranscriptionActive(true, user?.pid);

      // 5. Broadcast to other participants with full recording info
      this.meetingEventRouter?.emitLiveCaptionsToggleEvent(true, recordingInfo);

      trackTranscriptionStarted('standalone', participantCount);
      console.log('[MeetingService] ✅ Manual transcription started successfully');
    } catch (error) {
      console.error('[MeetingService] ❌ Failed to start manual transcription:', error);
      throw error;
    }

    // Note: The actual transcription streaming will be started automatically
    // by the auto-watcher when it detects the state change
  }

  /**
   * Stop manual transcription
   * This is a PUBLIC method that can be called from UI components
   * @param bypassInitiatorCheck - Allow non-initiators to stop (used when starting recording)
   * @param stopReason - Reported to analytics; null when another client reports this stop
   */
  async stopManualTranscription(
    bypassInitiatorCheck: boolean = false,
    stopReason: TranscriptionStopReason | null = 'user'
  ): Promise<void> {
    console.log('[MeetingService] 🛑 Stopping manual transcription');

    const { activeRecordingInfo, manualTranscriptionInitiatorPid, manualTranscriptionStartTime } =
      useRecordingStore.getState();
    const { user } = useWorkplaceStore.getState();
    const isInitiator = user?.pid === manualTranscriptionInitiatorPid;

    // Only allow initiator to stop, unless bypassing (e.g., starting a recording)
    if (!isInitiator && !bypassInitiatorCheck) {
      console.warn('[MeetingService] ⚠️ Only the initiator can stop manual transcription');
      return;
    }

    try {
      // Only the initiator should finalize the transcript
      if (isInitiator && activeRecordingInfo?.recordingPid) {
        console.log('[MeetingService] Finalizing standalone transcription...');

        // Stop collection and finalize (moves from Redis to S3)
        await transcriptCollectionService.stopCollection(true);

        // Transcript is now in S3 — safe to call AI endpoints
        this.triggerAiSummary(activeRecordingInfo.recordingPid).catch((err) => {
          Logger.error('❌ Failed to trigger AI summary (standalone):', err);
        });

        // Update recording status to completed
        try {
          await ApiService.request({
            method: 'PATCH',
            endpoint: `/recordings/${activeRecordingInfo.recordingPid}`,
            data: {
              status: 'completed',
            },
          });
        } catch (error) {
          console.error('[MeetingService] Failed to update recording status:', error);
        }

        // Clear active recording info
        useRecordingStore.getState().setActiveRecordingInfo(null);
      } else if (!isInitiator) {
        // Non-initiators just stop collection without finalizing
        await transcriptCollectionService.stopCollection(false);
      }

      // Update local state - this will trigger the auto-watcher
      useRecordingStore.getState().setManualTranscriptionActive(false);

      // Broadcast to other participants in the room
      this.meetingEventRouter?.emitLiveCaptionsToggleEvent(false);

      if (stopReason) {
        trackTranscriptionStopped(
          stopReason,
          manualTranscriptionStartTime ?? activeRecordingInfo?.recordingStartTime
        );
      }
      console.log('[MeetingService] ✅ Manual transcription stopped successfully');
    } catch (error) {
      console.error('[MeetingService] ❌ Error stopping manual transcription:', error);
    }

    // Note: The actual transcription streaming will be stopped automatically
    // by the auto-watcher when it detects the state change
  }

  async startJitsiRecording() {
    const { recordingSessionId } = useMeetingStore.getState();
    const { roomRecordingStatus } = useRecordingStore.getState();
    if (recordingSessionId && roomRecordingStatus === 'on') return;

    Swal.fire({
      title: 'Record Meeting',
      html: `
         <div style="display: flex; align-items: center;">
           <input type="checkbox" id="transRecording" checked style="margin-right: 5px;" />
           <label for="transRecording">Also generate a written transcript</label>
         </div>
       `,
      showCancelButton: true,
      confirmButtonText: 'Start Recording',
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: () => {
        const checkbox = document.getElementById('transRecording') as HTMLInputElement;
        return { canTranscribe: checkbox?.checked || false };
      },
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        const { canTranscribe } = result.value;

        // If there's an active standalone transcription, finalize it first
        const { isManualTranscriptionActive, activeRecordingInfo } = useRecordingStore.getState();
        const replacesTranscription =
          isManualTranscriptionActive && activeRecordingInfo?.type === 'standalone_transcription';

        if (replacesTranscription) {
          try {
            // Bypass initiator check when starting a recording
            await this.stopManualTranscription(true, 'recording_started');
          } catch (error) {
            console.error('[MeetingService] ❌ Failed to finalize manual transcription:', error);
            return;
          }
        }

        const sessionId = await jitsiService?.startMeetingRecording();
        if (sessionId) {
          this.saveRecordingEntry(sessionId, canTranscribe, replacesTranscription);

          if (!canTranscribe) {
            useRecordingStore.getState().setShowAiNoTranscriptionBanner(true);
          }
        }
      }
    });
  }

  showLeaveRoomAlert(): Promise<'exit' | 'continue'> {
    return Swal.fire({
      title: 'Leaving Room',
      text: 'You have an active recording session. Would you like to stop and save the recording and leave the room, or remain in the room and continue to record this meeting?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Save And Exit',
      cancelButtonText: 'Continue Recording',
      reverseButtons: true,
      allowOutsideClick: false,
    }).then((result) => (result.isConfirmed ? 'exit' : 'continue'));
  }

  showLeaveRoomAlertForCaptions(): Promise<'exit' | 'continue'> {
    return Swal.fire({
      title: 'Leaving Room',
      text: 'You have active live captions running. Would you like to stop and save the captions and leave the room, or remain in the room and continue generating captions?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Save And Exit',
      cancelButtonText: 'Continue Captions',
      reverseButtons: true,
      allowOutsideClick: false,
    }).then((result) => (result.isConfirmed ? 'exit' : 'continue'));
  }

  /**
   * Shows the recording title prompt using Swal styled to match RecordingUpdateDialog
   * Uses the same format and styling as the Material-UI dialog component
   * @returns Promise that resolves with the entered title or null if cancelled
   */
  async showRecordingTitlePrompt(): Promise<string | null> {
    const { activeRecordingInfo } = useRecordingStore.getState();

    // Use the same formatDateTime function as RecordingUpdateDialog
    const defaultTitle =
      activeRecordingInfo?.title || formatDateTime(activeRecordingInfo?.recordingStartTime);

    const result = await Swal.fire({
      title: 'Update Recording Details',
      html: `
        <div style="text-align: left; margin-top: 8px;">
          <label for="swal-title-input" style="
            font-size: 12px;
            color: rgba(0, 0, 0, 0.6);
            display: block;
            margin-bottom: 4px;
            font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
          ">Title</label>
          <input
            id="swal-title-input"
            class="swal2-input"
            value="${defaultTitle}"
            style="
              width: 100%;
              padding: 8px 0px;
              margin: 0;
              box-sizing: border-box;
              border: none;
              border-bottom: 1px solid rgba(0, 0, 0, 0.42);
              border-radius: 0;
              font-size: 16px;
              font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
            "
            onfocus="this.style.borderBottom='2px solid #1976d2'"
            onblur="this.style.borderBottom='1px solid rgba(0, 0, 0, 0.42)'"
          />
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Save',
      cancelButtonText: 'Cancel',
      allowOutsideClick: false,
      didOpen: () => {
        const input = document.getElementById('swal-title-input') as HTMLInputElement;
        if (input) {
          input.focus();
          // Move cursor to end
          input.setSelectionRange(input.value.length, input.value.length);
        }
      },
      preConfirm: () => {
        const input = document.getElementById('swal-title-input') as HTMLInputElement;
        const value = input?.value?.trim();
        if (!value) {
          Swal.showValidationMessage('Title is required');
          return false;
        }
        return value;
      },
    });

    if (result.isConfirmed && result.value) {
      return result.value;
    }

    return null;
  }

  /**
   * Ends recording session with a prompt to enter title
   * Shows the styled Swal prompt for the user to enter/confirm the title before stopping
   */
  async endRecordingSessionWithPrompt(stopReason: RecordingStopReason = 'user'): Promise<void> {
    const title = await this.showRecordingTitlePrompt();

    if (title) {
      await this.endRecordingSession(title, stopReason);
    }
    // If title is null (user cancelled), do nothing - keep recording active
  }

  async saveRecordingEntry(
    sessionId: string,
    canTranscribe: boolean,
    replacesTranscription = false
  ) {
    const { setActiveRecordingInfo } = useRecordingStore.getState();

    const { currentRoomId, getAllParticipantIds } = useMeetingStore.getState();
    const room_pid = currentRoomId?.match(/meeting-(.+)$/)?.[1];

    // Get participant count for speaker diarization
    const participantCount = getAllParticipantIds().length;

    try {
      const response = await ApiService.request<any>({
        method: 'POST',
        endpoint: '/recordings',
        data: {
          room_pid,
          recording_session_id: sessionId,
          can_transcribe: canTranscribe,
          participant_count: participantCount,
        },
      });

      setActiveRecordingInfo({
        name: response.name,
        avatarUrl: response.avatar_url,
        initiatorUserPid: response.user_pid,
        recordingStartTime: response.created_at,
        recordingPid: response.recording_pid,
        recordingRoomPid: room_pid,
        title: response.title,
        can_transcribe: canTranscribe, // Include can_transcribe flag for auto-streaming watcher
      });
      trackRecordingStarted({
        withTranscript: canTranscribe,
        participantCount,
        replacedTranscription: replacesTranscription,
      });
    } catch (error) {
      Logger.error('❌ Failed to save recording entry:', error);
    }
  }

  /** @param stopReason - Reported to analytics; null when another client reports this stop */
  async endRecordingSession(
    title: string,
    stopReason: RecordingStopReason | null = 'user'
  ): Promise<void> {
    try {
      const { activeRecordingInfo } = useRecordingStore.getState();
      await ApiService.request<any>({
        method: 'PATCH',
        endpoint: `/recordings/${activeRecordingInfo.recordingPid}`,
        data: { title },
      });
      const stopped = await jitsiService?.stopMeetingRecording();
      if (stopped && stopReason) trackRecordingStopped(stopReason, activeRecordingInfo);
      useWorkplaceStore.getState().dismissDrawerView();
    } catch (error) {
      await jitsiService?.stopMeetingRecording();
      Logger.error('❌ Failed to end recording session:', error);
    }
  }

  private async triggerAiSummary(recordingPid: string): Promise<void> {
    const MAX_ATTEMPTS = 20;
    const POLL_INTERVAL_MS = 10_000;

    useRecordingStore.getState().setIsAiSummaryGenerating(true);
    Logger.debug('[MeetingService] Waiting for transcription to be available...', recordingPid);

    try {
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const response = await ApiService.get<any>(`recordings/${recordingPid}/download-url`);
          if (response?.transcription_url) {
            Logger.debug(
              `[MeetingService] Transcription ready (attempt ${attempt}), triggering AI summary...`
            );
            await Promise.allSettled([
              ApiService.get(`ai/recordings/${recordingPid}/summarize-transcription`),
              ApiService.get(`ai/recordings/${recordingPid}/extract-assignments`),
            ]);
            Logger.debug('✅ AI summary triggered for recording:', recordingPid);

            try {
              await ApiService.get(`ai/recordings/${recordingPid}/send-summary-email`);
              Logger.debug('✅ Summary email sent for recording:', recordingPid);
            } catch (err) {
              Logger.error('[MeetingService] ❌ Failed to send summary email:', err);
            }

            return;
          }
        } catch (err) {
          Logger.debug(`[MeetingService] Poll attempt ${attempt} failed:`, err);
        }

        if (attempt < MAX_ATTEMPTS) {
          await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        }
      }

      Logger.error(
        '[MeetingService] ❌ Transcription not available after max attempts, skipping AI summary.'
      );
    } finally {
      useRecordingStore.getState().setIsAiSummaryGenerating(false);
    }
  }

  async loadRoomRecordingInfo(recordingSessionId: string) {
    if (!recordingSessionId) return;

    // The initiator creates the recording row only after jicofo returns the session id,
    // so participants can ask for it first. /by_session answers 200 with an empty body
    // in that case, which means "not written yet" — retry instead of storing it.
    const RETRY_DELAYS_MS = [0, 500, 1000, 2000, 4000];

    for (const delay of RETRY_DELAYS_MS) {
      if (delay) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        // Recording stopped or another session took over while waiting.
        if (useMeetingStore.getState().recordingSessionId !== recordingSessionId) return;
      }

      let response: any;
      try {
        response = await ApiService.request<any>({
          method: 'GET',
          endpoint: `/recordings/by_session/${recordingSessionId}`,
        });
      } catch (error) {
        Logger.warn('[Recording] by_session lookup failed, retrying...', error);
        continue;
      }

      if (!response?.recording_pid) {
        Logger.debug('[Recording] Recording row not written yet, retrying...', recordingSessionId);
        continue;
      }

      useRecordingStore.getState().setActiveRecordingInfo({
        name: response.name,
        avatarUrl: response.avatar_url,
        initiatorUserPid: response.user_pid,
        recordingStartTime: response.created_at,
        recordingPid: response.recording_pid,
        title: response.title,
        can_transcribe: response.can_transcribe, // Include can_transcribe for other participants
        recordingRoomPid: response.room_pid,
      });

      useMeetingStore.getState().setRecordingSessionId(recordingSessionId);
      return;
    }

    Logger.error('[Recording] Gave up waiting for recording row:', recordingSessionId);
  }

  handleLiveCaptionsToggle = async (event: DynamicEvent, retryCount: number = 0) => {
    const { user_pid, room_pid, enabled, recording_info } = event as any;
    const { getCurrentRoomPid } = useMeetingStore.getState();
    const { user } = useWorkplaceStore.getState();
    const currentUserPid = user?.pid;
    const currentRoom = getCurrentRoomPid();

    // Check if rooms match
    const roomsMatch = room_pid === getCurrentRoomPid();

    // Only retry if currentRoom is not initialized yet (null/undefined/empty)
    // If currentRoom exists but doesn't match, that's a legitimate skip (user in different room)
    const isRoomNotInitialized = !currentRoom;

    // If rooms don't match AND room is not initialized yet, retry after delay
    if (!roomsMatch && isRoomNotInitialized && retryCount < 5) {
      const retryDelay = [500, 1000, 1500, 2000, 2500][retryCount]; // Progressive delay up to ~7.5 seconds total
      setTimeout(() => {
        this.handleLiveCaptionsToggle(event, retryCount + 1);
      }, retryDelay);
      return;
    }

    // If rooms don't match but currentRoom IS initialized, it's a legitimate mismatch - don't retry
    if (!roomsMatch && !isRoomNotInitialized) {
      return;
    }

    // Only process the event if it's for the current room
    if (room_pid === getCurrentRoomPid()) {
      console.log('[MeetingService] Live captions toggle received:', {
        user_pid,
        room_pid,
        enabled,
        recording_info,
        is_from_self: user_pid === currentUserPid,
      });

      // Check current local transcription state
      const { isManualTranscriptionActive } = useRecordingStore.getState();
      const isStateRestoration = isManualTranscriptionActive !== enabled;

      // Skip ONLY if:
      // 1. This is our own event (same user_pid)
      // 2. AND our local state already matches (not a restoration scenario)
      if (user_pid === currentUserPid && !isStateRestoration) {
        return;
      }

      // If this is a standalone transcription being enabled, set the recording info
      if (enabled && recording_info) {
        console.log('[MeetingService] Setting up transcription recording info for participant');

        // Use the recording info from the event (no need to fetch)
        useRecordingStore.getState().setActiveRecordingInfo({
          name: recording_info.initiator_name,
          avatarUrl: recording_info.initiator_avatar,
          initiatorUserPid: user_pid,
          recordingStartTime: recording_info.recorded_at,
          recordingPid: recording_info.recording_pid,
          recordingRoomPid: room_pid,
          title: recording_info.title,
          can_transcribe: recording_info.can_transcribe,
          type: recording_info.type,
        });

        // IMPORTANT: Only set manual transcription active for standalone_transcription type
        // If this is a regular recording with can_transcribe=true, transcription is controlled by recording
        if (recording_info.type === 'standalone_transcription') {
          if (!isManualTranscriptionActive && isCurrentUserGuest()) {
            GlobalAudioNotificationService.play('blow');
            useNotificationStore.getState().showSnackbar('Live captions started', 'success');
          }
          useRecordingStore.getState().setManualTranscriptionActive(enabled, user_pid);
          console.log(
            '[MeetingService] ✅ Manual transcription state set for standalone transcription'
          );
        } else {
          console.log(
            '[MeetingService] ℹ️ Recording-based transcription - manual transcription NOT set (controlled by recording)'
          );
        }
      } else if (!enabled) {
        if (isManualTranscriptionActive && isCurrentUserGuest()) {
          useNotificationStore.getState().showSnackbar('Live captions stopped', 'info');
        }
        useRecordingStore.getState().setManualTranscriptionActive(false);
        useRecordingStore.getState().setActiveRecordingInfo(null);
      }

      // Also update UI state for consistency
      const { setIsEnabled } = useLiveCaptionsStore.getState();
      setIsEnabled(enabled);

      console.log(
        `[MeetingService] ✅ Manual transcription ${enabled ? 'started' : 'stopped'} by ${user_pid} - auto-watcher will handle streaming`
      );
    }
  };

  private async startStreamingTranscription(roomPid: string): Promise<void> {
    try {
      const { setTranscriptionStatus } = useLiveCaptionsStore.getState();
      const { user } = useWorkplaceStore.getState();
      const { activeRecordingInfo } = useRecordingStore.getState();

      if (!user) {
        Logger.error('[MeetingService] No user found for transcription');
        return;
      }

      // CRITICAL FIX: Wait for local audio track to be available
      // This handles the race condition where late joiners try to start transcription
      // before their local tracks are initialized
      try {
        Logger.debug(
          '[MeetingService] 🎯 Waiting for local audio track before starting transcription...'
        );
        const audioTrack = await this.waitForLocalAudioTrack(5000); // 5 second timeout

        if (audioTrack) {
          assemblyAIStreamingService.setJitsiAudioTrack(audioTrack);
          Logger.debug('[MeetingService] ✅ Audio track set for transcription');
        } else {
          throw new Error('Audio track is null');
        }
      } catch (error) {
        Logger.error('[MeetingService] ❌ Failed to get audio track:', error);
        setTranscriptionStatus('error');

        // Show user-friendly error
        const errorMessage =
          error instanceof Error ? error.message : 'Could not access microphone for transcription';

        Logger.error(`[MeetingService] ${errorMessage}`);

        // Don't proceed with transcription if no audio available
        return;
      }

      // Start recording-mode transcript collection when a recording is active
      if (activeRecordingInfo?.recordingPid) {
        transcriptCollectionService.startCollection(activeRecordingInfo.recordingPid, {
          onFlushSuccess: (count) => {
            Logger.debug(`[MeetingService] ✅ Transcript batch saved: ${count} segments`);
          },
          onFlushError: (error, count) => {
            Logger.error(
              `[MeetingService] ❌ Failed to save transcript batch (${count} segments):`,
              error
            );
          },
        });
      }

      // Start Assembly AI transcription
      await assemblyAIStreamingService.startTranscription(roomPid, {
        onTranscript: (transcriptionEvent: TranscriptionEvent) => {
          this.handleTranscriptionResult(transcriptionEvent);
        },
        onError: (error: Error) => {
          Logger.error('[MeetingService] Transcription error:', error);
          setTranscriptionStatus('error');
        },
        onStatusChange: (status) => {
          setTranscriptionStatus(status);
        },
      });

      Logger.debug('[MeetingService] ✅ Streaming transcription started');
    } catch (error) {
      Logger.error('[MeetingService] ❌ Failed to start streaming transcription:', error);
      useLiveCaptionsStore.getState().setTranscriptionStatus('error');
    }
  }

  private async stopStreamingTranscription(): Promise<void> {
    try {
      // Stop transcript collection first (ensures final flush happens)
      if (transcriptCollectionService.isCollecting()) {
        // Only the recording initiator should finalize the transcript
        const { activeRecordingInfo } = useRecordingStore.getState();
        const { user } = useWorkplaceStore.getState();
        const isRecordingInitiator = activeRecordingInfo?.initiatorUserPid === user?.pid;

        Logger.debug('[MeetingService] Stopping transcript collection:', {
          isRecordingInitiator,
          initiatorPid: activeRecordingInfo?.initiatorUserPid,
          currentUserPid: user?.pid,
        });

        // stopCollection awaits finalizeTranscript() (Redis → S3) before resolving
        await transcriptCollectionService.stopCollection(isRecordingInitiator);

        // Transcript is now in S3 — safe to call AI endpoints
        if (
          isRecordingInitiator &&
          activeRecordingInfo?.can_transcribe &&
          activeRecordingInfo?.recordingPid
        ) {
          this.triggerAiSummary(activeRecordingInfo.recordingPid).catch((err) => {
            Logger.error('❌ Failed to trigger AI summary:', err);
          });
        }
      }

      await assemblyAIStreamingService.stopTranscription();

      // Update transcription status to idle
      useLiveCaptionsStore.getState().setTranscriptionStatus('idle');

      console.log('[MeetingService] ✅ Streaming transcription stopped');
    } catch (error) {
      Logger.error('[MeetingService] ❌ Failed to stop streaming transcription:', error);
    }
  }

  private handleTranscriptionResult = (transcriptionEvent: TranscriptionEvent) => {
    const { addTranscript, updateCurrentTranscript } = useLiveCaptionsStore.getState();
    const { user, org } = useWorkplaceStore.getState();
    const { currentFloorPresence } = useMultiFloorStateStore.getState();
    const { getCurrentRoomPid } = useMeetingStore.getState();

    try {
      // Create transcript entry
      const transcriptEntry: TranscriptEntry = {
        id: nanoid(),
        text: transcriptionEvent.text,
        confidence: transcriptionEvent.confidence,
        isFinal: transcriptionEvent.isFinal,
        speaker: user?.name || 'Unknown Speaker',
        timestamp: transcriptionEvent.timestamp,
        roomPid: transcriptionEvent.roomPid,
      };

      // Update local store and collect for backend
      if (transcriptionEvent.isFinal) {
        addTranscript(transcriptEntry);

        // Trigger translation if enabled (for own transcripts too)
        this.handleTranslation(transcriptEntry);

        // Collect transcript segment for recording backend storage (only final transcriptions)
        if (transcriptCollectionService.isCollecting()) {
          const eventWithSpeaker: TranscriptionEvent = {
            ...transcriptionEvent,
            speaker: user?.name || 'Unknown Speaker',
          };
          transcriptCollectionService.addSegment(eventWithSpeaker);
        }
      } else {
        updateCurrentTranscript(transcriptionEvent.text);
      }

      // Broadcast to other participants in the room via events service
      if (!this.meetingEventRouter) {
        throw Error(
          'Meeting event router not initialized. Ensure the meeting service is properly set up before attempting to broadcast transcriptions.'
        );
      }

      const missingKey = Object.values(transcriptEntry).find(
        (key) => transcriptEntry[key as keyof TranscriptEntry]
      );

      if (missingKey) {
        throw Error(`${missingKey}`);
      }

      Logger.debug(
        '[MeetingService] 📤 About to emit transcription - event_id:',
        transcriptEntry.id
      );

      this.meetingEventRouter.emitStreamingTranscriptionEvent({
        event_id: transcriptEntry.id,
        user_pid: user?.pid || '',
        room_pid: getCurrentRoomPid() || '',
        floor_pid: currentFloorPresence || '',
        org_pid: org?.pid || '',
        transcript_data: {
          id: transcriptEntry.id,
          text: transcriptEntry.text,
          confidence: transcriptEntry.confidence,
          isFinal: transcriptEntry.isFinal,
          speaker: transcriptEntry.speaker,
          timestamp: transcriptEntry.timestamp,
        },
      });
    } catch (error) {
      Logger.error('[MeetingService] ❌ Error handling transcription result:', error);
    }
  };

  handleStreamingTranscription = (event: DynamicEvent) => {
    const { user_pid, room_pid, transcript_data } = event as any;
    const { getCurrentRoomPid } = useMeetingStore.getState();
    const { user } = useWorkplaceStore.getState();

    const currentRoomPid = getCurrentRoomPid();
    const currentUserPid = user?.pid;

    Logger.debug('[MeetingService] 📥 Transcription event received:', {
      event_user_pid: user_pid,
      event_room_pid: room_pid,
      current_room_pid: currentRoomPid,
      current_user_pid: currentUserPid,
      text: transcript_data?.text,
      is_same_room: room_pid === currentRoomPid,
      is_different_user: user_pid !== currentUserPid,
      will_process: room_pid === currentRoomPid && user_pid !== currentUserPid,
    });

    // Only process transcriptions from other users in the same room
    if (room_pid === getCurrentRoomPid() && user_pid !== user?.pid) {
      Logger.debug('[MeetingService] ✅ Processing remote transcription');

      const { addTranscript } = useLiveCaptionsStore.getState();

      // Add the remote transcript to our local store
      const transcriptEntry: TranscriptEntry = {
        id: transcript_data.id,
        text: transcript_data.text,
        confidence: transcript_data.confidence,
        isFinal: transcript_data.isFinal,
        speaker: transcript_data.speaker || 'Remote Speaker',
        timestamp: transcript_data.timestamp,
        roomPid: room_pid,
      };

      addTranscript(transcriptEntry);

      // Trigger translation if enabled (for remote transcripts)
      if (transcript_data.isFinal) {
        this.handleTranslation(transcriptEntry);
      }
    } else {
      Logger.debug('[MeetingService] ⏭️ Skipping transcription (own message or different room)');
    }
  };

  /**
   * Handle translation for a transcript entry
   * @param transcriptEntry - The transcript to translate
   */
  private async handleTranslation(transcriptEntry: TranscriptEntry): Promise<void> {
    const {
      translationEnabled,
      targetLanguage,
      setTranscriptTranslating,
      updateTranscriptTranslation,
    } = useLiveCaptionsStore.getState();

    // Skip if translation is not enabled or target is original
    if (!translationEnabled || targetLanguage === 'original') {
      return;
    }

    try {
      // Mark as translating
      setTranscriptTranslating(transcriptEntry.id, true);

      Logger.debug('[MeetingService] 🌐 Translating transcript:', {
        id: transcriptEntry.id,
        targetLanguage,
        text: transcriptEntry.text.substring(0, 50),
      });

      // Translate the text
      const translatedText = await translationService.translateText(
        transcriptEntry.text,
        targetLanguage
      );

      // Update the transcript with translation
      updateTranscriptTranslation(transcriptEntry.id, translatedText);

      Logger.debug('[MeetingService] ✅ Translation completed:', {
        id: transcriptEntry.id,
        translatedText: translatedText.substring(0, 50),
      });
    } catch (error) {
      Logger.error('[MeetingService] ❌ Translation failed:', error);
      // Mark as not translating on error
      setTranscriptTranslating(transcriptEntry.id, false);
    }
  }
}
